import type { KeyboardEvent, ReactNode } from 'react';

import { Combobox } from '../../components/Combobox';
import { Input, Select } from '../../components/ui';
import { useManagerStatusDictionary } from '../../api/manager-statuses';
import { useMailingListIndex, useTransfertValues } from '../../api/applications';
import { usePrograms, useVenueDirectory } from '../quotas/dictionary';
import { venueComboboxOptions } from '../quotas/quotas-model';
import { departmentFilterOptions } from '../../departments';
import { useDepartmentJoin } from '../../departments-query';
import {
  AUDIENCE_FILTER_OPTIONS,
  AUDIENCE_LABEL,
  AGE_GROUP_FILTER_OPTIONS,
  CONTRACT_FILTER_OPTIONS,
  DISTRIBUTED_FILTER_OPTIONS,
  ENROLLED_FILTER_OPTIONS,
  FORMAT_FILTER_OPTIONS,
  FTP_FILTER_OPTIONS,
  LEGAL_REP_CONSENT_FILTER_OPTIONS,
  NO_VENUE_OPTION,
  PARAM,
  SIGNER_MATCHES_FILTER_OPTIONS,
  TRANSFERT_FILTER_NONE,
  TRANSFERT_NONE_LABEL,
  UNTI_API_FILTER_OPTIONS,
  VENUE_FILTER_NONE,
  filterAppliedClass,
  mailingListFilterOptions,
  programShortOptions,
  statusFilterOptions,
  transfertFilterOptions,
  venueFilterPatch,
  venueFilterValue,
} from './applications-model';
import {
  managerStatusFilterOptions,
  managerStatusFilterPatch,
  managerStatusFilterValue,
} from './manager-statuses-model';

import './applications.css';

interface FilterBarProps {
  /** The URL params, as `useQueryParams` hands them over. */
  params: Record<string, string>;
  /** Commits one filter; the page resets `page` around it. */
  onFilter: (key: string, value: string | undefined) => void;
  /**
   * ADDITIVE (v1.8.0, Р9/#3, T.Y10) — commits SEVERAL filters at once. The «Статус
   * менеджера» control needs it: `managerStatus` and `managerStatusId` must swap in
   * one URL write, because two back-to-back `onFilter` calls each rebuild the URL
   * from the same render-time snapshot and the second would silently drop the first.
   */
  onFilterPatch?: (patch: Record<string, string | undefined>) => void;
  onReset: () => void;
  /**
   * T.Z219 — the «Колонки» control, rendered at the end of the row next to
   * «Сбросить». It is NOT a filter (its state lives in `localStorage`, not the
   * URL, and the T.Z192 applied tint does not apply), so it travels in as a slot
   * instead of a fifth callback family — the bar stays a filter surface, the
   * visibility state stays the page's.
   */
  columnsControl?: ReactNode;
  /**
   * The venue NAMES of the page the bar sits on, as `venueId` → name. It is the
   * fallback that keeps a scoped link honest when the venue dictionary (#6) has
   * not answered yet or does not know the id any more: the input then prints the
   * name of the venue the rows actually show instead of reading as «nothing
   * selected» (the same rescue the quota grid applies, T.R4).
   */
  venueNames?: ReadonlyMap<number, string>;
}

const TRI_STATE: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Все' },
  { value: 'true', label: 'Да' },
  { value: 'false', label: 'Нет' },
];

/**
 * The filter bar of И3. Every control writes straight into the URL (`useQueryParams`),
 * so a reload or a shared link restores the view — the dashboard's rule.
 *
 * «Площадка» leads the bar, on a full-width row of its own: the id it used to ask
 * for was a dictionary key nobody knows by heart (the TODO that finally ran out),
 * and it is now the T.R4 type-ahead over the venue directory (#6) the quotas and
 * teachers pages already use. The URL param is still `venueId`, so every
 * drill-down link into this page keeps working.
 *
 * Selects commit on change; the free-text fields commit on Enter or blur, because a
 * request per keystroke would hammer the api for a filter the user is still typing.
 *
 * T.Z192 — every control derives its «applied» class from the same URL value it
 * renders (`filterAppliedClass`), so a set filter wears the warm tint and
 * «Сбросить» clears the tints by clearing the state — the hint is never its own
 * state to forget.
 */
export function ApplicationsFilterBar({
  params,
  onFilter,
  onFilterPatch,
  onReset,
  venueNames,
  columnsControl,
}: FilterBarProps) {
  return (
    <div className="filter-bar" role="search" aria-label="Фильтры заявлений">
      {/* T.Z20 — the venue control commits through the patch too: «Без площадки» and a
          venue id are mutually exclusive, and that swap has to be ONE URL write. */}
      <VenueFilter params={params} venueNames={venueNames} onFilter={onFilter} onFilterPatch={onFilterPatch} />

      <div className="filter-bar__row">
        <div className="filter-bar__field filter-bar__field--q">
          <label className="filter-bar__label" htmlFor="applications-q">
            Поиск
          </label>
          {/* T.Z226 — the box takes an ENUMERATION: «a,b», «a b», «a, b» — the union of
              the matches, the api's own v1.9.80 grammar. The input carries the raw
              string (the URL mirrors it as typed), so the web has nothing to split. */}
          <CommitInput
            id="applications-q"
            testId="filter-q"
            value={params[PARAM.Q] ?? ''}
            placeholder="ФИО — целиком; ЕПГУ ID, УНТИ ID, телефон, email — несколько через запятую или пробел"
            onCommit={(value) => onFilter(PARAM.Q, value)}
          />
        </div>

        <div className="filter-bar__field">
          <label className="filter-bar__label" htmlFor="applications-status">
            Статус
          </label>
          <Select
            id="applications-status"
            className={filterAppliedClass(params[PARAM.STATUS])}
            data-testid="filter-status"
            value={params[PARAM.STATUS] ?? ''}
            onChange={(event) => onFilter(PARAM.STATUS, event.target.value || undefined)}
          >
            {/* ADDITIVE (v1.9.33, T.Z141) — the option list comes from the model, so a
                multi-value arrival (the ЦА cell drill-down's `LINK_RECEIVED,EVI_PASSED`)
                renders as its own SELECTED preset option instead of reading as «Все
                статусы»; picking any single option replaces the preset. */}
            {statusFilterOptions(params[PARAM.STATUS]).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-course">
            Курс (ID)
          </label>
          <CommitInput
            id="applications-course"
            testId="filter-courseId"
            inputMode="numeric"
            value={params[PARAM.COURSE_ID] ?? ''}
            placeholder="ID"
            onCommit={(value) => onFilter(PARAM.COURSE_ID, value || undefined)}
          />
        </div>

        {/* ADDITIVE (v1.8.8, T.Z43) — «Программа»: the owner's six short names over the
            course dictionary (#21). The api resolves the caption to its офл/онл pair, so
            the register never asks the user for a course id. */}
        <ProgramShortFilter params={params} onFilter={onFilter} />

        {/* ADDITIVE (v1.8.8, T.Z43) — «Аудитория»: the three campaign buckets, resolved
            through `programs.audience` server-side. Composes with everything above. */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-audience">
            Аудитория
          </label>
          <Select
            id="applications-audience"
            className={filterAppliedClass(params[PARAM.AUDIENCE])}
            data-testid="filter-audience"
            value={params[PARAM.AUDIENCE] ?? ''}
            onChange={(event) => onFilter(PARAM.AUDIENCE, event.target.value || undefined)}
          >
            <option value="">Любая</option>
            {AUDIENCE_FILTER_OPTIONS.map((audience) => (
              <option key={audience} value={audience}>
                {AUDIENCE_LABEL[audience] ?? audience}
              </option>
            ))}
          </Select>
        </div>

        {/* ADDITIVE (v1.8.9, T.Z56) — «Отдел»: the venue department through its managers.
            SERVER-SIDE on this page (the api resolves the bucket into the venue set and
            narrows rows + total), so this select only writes the URL key. */}
        <DepartmentFilter params={params} onFilter={onFilter} />

        <div className="filter-bar__field">
          <label className="filter-bar__label" htmlFor="applications-region">
            Регион
          </label>
          <CommitInput
            id="applications-region"
            testId="filter-region"
            value={params[PARAM.REGION] ?? ''}
            placeholder="Название региона"
            onCommit={(value) => onFilter(PARAM.REGION, value || undefined)}
          />
        </div>

        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-has-venue">
            С площадкой
          </label>
          <Select
            id="applications-has-venue"
            className={filterAppliedClass(params[PARAM.HAS_VENUE])}
            data-testid="filter-hasVenue"
            value={params[PARAM.HAS_VENUE] ?? ''}
            onChange={(event) => onFilter(PARAM.HAS_VENUE, event.target.value || undefined)}
          >
            {TRI_STATE.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.21, T.Z101) — «Договор»: the three fact buckets the worker's
          contract sync writes (подписан / не подписан / не пришёл). SERVER-SIDE on this
          page — the api narrows rows AND the pagination total, the select only writes
          the URL key.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-contract">
            Договор
          </label>
          <Select
            id="applications-contract"
            className={filterAppliedClass(params[PARAM.CONTRACT])}
            data-testid="filter-contract"
            value={params[PARAM.CONTRACT] ?? ''}
            onChange={(event) => onFilter(PARAM.CONTRACT, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {CONTRACT_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.49, T.Z169) — «Согласие родителей»: the two buckets the
          worker's contract sync writes (Отмечено / Не отмечено). SERVER-SIDE on this
          page exactly like `contract` — the api narrows rows AND the pagination total,
          the select only writes the URL key. Deliberately TWO options, not three: a
          row the sync has no answer for (`legalRepConsent: null`, «—») matches
          NEITHER value, so «нет данных» can never be folded into «не отмечено».
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-consent">
            Согласие родителей
          </label>
          <Select
            id="applications-consent"
            className={filterAppliedClass(params[PARAM.LEGAL_REP_CONSENT])}
            data-testid="filter-legalRepConsent"
            value={params[PARAM.LEGAL_REP_CONSENT] ?? ''}
            onChange={(event) => onFilter(PARAM.LEGAL_REP_CONSENT, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {LEGAL_REP_CONSENT_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?»: the signer-verdict
          buckets the worker's contract sync writes (Да = the person who pressed
          «Подписать» in iSmart is our child; Нет = somebody else — the CSV audit's
          12-of-2810 case). SERVER-SIDE on this page exactly like `contract` — the api
          narrows rows AND the pagination total, the select only writes the URL key.
          Deliberately TWO options, not three: a row the sync has no verdict for
          (`signerMatches: null` — no signer, no our name, or unsigned) matches
          NEITHER value, so «—» can never be folded into «Нет».
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-signer-matches">
            Договор заключен ПП?
          </label>
          <Select
            id="applications-signer-matches"
            className={filterAppliedClass(params[PARAM.SIGNER_MATCHES])}
            data-testid="filter-signerMatches"
            value={params[PARAM.SIGNER_MATCHES] ?? ''}
            onChange={(event) => onFilter(PARAM.SIGNER_MATCHES, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {SIGNER_MATCHES_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.52, T.Z176) — «Зачислен»: the cat.2035.university mirror's
          own word (the unti2035 sweep's fact — their `status` literal folds to
          `accepted`; the owner's rule «Да = ACCEPTED, нет = everything else»).
          SERVER-SIDE on this page exactly like `contract` — the api narrows rows AND
          the pagination total, the select only writes the URL key. Deliberately TWO
          options, not three: a row the sweep has not covered (`enrolled: null` — no
          `unti_id`, or no received page has named it) matches NEITHER value, so «—»
          can never be folded into «Нет». NOT the same fact as «Договор»'s
          `enrolled` literal — that one reads the iSmart platform's own word.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-enrolled">
            Зачислен
          </label>
          <Select
            id="applications-enrolled"
            className={filterAppliedClass(params[PARAM.ENROLLED])}
            data-testid="filter-enrolled"
            value={params[PARAM.ENROLLED] ?? ''}
            onChange={(event) => onFilter(PARAM.ENROLLED, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {ENROLLED_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — «Распределён»: the child's class
          fact — the platform mirror's answer (`platform_distributions`, the
          worker's distribution-sync pass) with our live study groups as the
          fallback. SERVER-SIDE on this page exactly like `enrolled` — the api
          narrows rows AND the pagination total, the select only writes the URL
          key. Deliberately TWO options, not three: a row with NO signal from
          either side (no mirror row, no our group) matches NEITHER value, so «—»
          can never be folded into «Нет». «Да» does NOT mean «материалы открыты» —
          their advisory `is_distributed` flag never feeds this predicate.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-distributed">
            Распределён
          </label>
          <Select
            id="applications-distributed"
            className={filterAppliedClass(params[PARAM.DISTRIBUTED])}
            data-testid="filter-distributed"
            value={params[PARAM.DISTRIBUTED] ?? ''}
            onChange={(event) => onFilter(PARAM.DISTRIBUTED, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {DISTRIBUTED_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.25, T.Z113) — «Провалил ЕВИ»: the auto fact the worker's
          registry engine writes (the child vanished from the fresh ЕВИ pass list with
          no FTP evidence; the MANUAL flag lives in the «Флаги» wording, this select
          reads the fact). SERVER-SIDE on this page, exactly like `contract` — the api
          narrows rows AND the pagination total, the select only writes the URL key the
          dashboard's red widget drills in through. `false` is the explicit
          «не провалил» view, so the value must travel even when it is `false`.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-evi-failed">
            Провалил ЕВИ
          </label>
          <Select
            id="applications-evi-failed"
            className={filterAppliedClass(params[PARAM.EVI_FAILED])}
            data-testid="filter-eviFailed"
            value={params[PARAM.EVI_FAILED] ?? ''}
            onChange={(event) => onFilter(PARAM.EVI_FAILED, event.target.value || undefined)}
          >
            <option value="">Все</option>
            <option value="true">Провалил ЕВИ</option>
            <option value="false">Не провалил</option>
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.48, T.Z168) — «Возраст»: exactly the owner's three buckets.
          SERVER-SIDE on this page, exactly like `contract` — the api narrows rows AND
          the pagination total (the birth-date range of the read instant), the select
          only writes the URL key. A row with no birth date yet matches NO bucket, so
          the filtered view is smaller than the unfiltered one by design.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-age-group">
            Возраст
          </label>
          <Select
            id="applications-age-group"
            className={filterAppliedClass(params[PARAM.AGE_GROUP])}
            data-testid="filter-ageGroup"
            value={params[PARAM.AGE_GROUP] ?? ''}
            onChange={(event) => onFilter(PARAM.AGE_GROUP, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {AGE_GROUP_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.56, T.Z186) — «Рассылка»: the Списки рассылок (#34) by NAME.
          SERVER-SIDE on this page exactly like `contract` — the api narrows rows AND
          the pagination total to the list's CURRENT recipients (the «Выбывшие» are the
          list's own exclusion), the select only writes the URL key. The owner reads
          here after a send: who was mailed, who signed since, who left the list.
        */}
        <MailingListFilter params={params} onFilter={onFilter} />

        {/*
          ADDITIVE (v1.9.56, T.Z186) — «FTP»: the УНТИ key the FTP sync fills. Есть =
          `unti_id IS NOT NULL`, Нет = `unti_id IS NULL`; the two values partition the
          register, so there is no third option. SERVER-SIDE like `contract`, and it
          composes with «Рассылка» — «был в рассылке, а УНТИ-листа нет» is one URL.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-ftp">
            FTP
          </label>
          <Select
            id="applications-ftp"
            className={filterAppliedClass(params[PARAM.FTP])}
            data-testid="filter-ftp"
            value={params[PARAM.FTP] ?? ''}
            onChange={(event) => onFilter(PARAM.FTP, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {FTP_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.66, T.Z203) — «УНТИ API»: the row's linkage to the УНТИ
          mirror, four buckets (найден / не найден по epgu_id + unti_id / не найден
          epgu_id / не найден unti_id). SERVER-SIDE on this page exactly like
          `contract` — the api narrows rows AND the pagination total against the
          mirror (in two phases; the select only writes the URL key), and the active
          filter rides to the export, which narrows the file with the screen and
          gains no new column. The labels are the owner's own words for the buckets.
        */}
        <div className="filter-bar__field">
          <label className="filter-bar__label" htmlFor="applications-unti-api">
            УНТИ API
          </label>
          <Select
            id="applications-unti-api"
            className={filterAppliedClass(params[PARAM.UNTI_API])}
            data-testid="filter-untiApi"
            value={params[PARAM.UNTI_API] ?? ''}
            onChange={(event) => onFilter(PARAM.UNTI_API, event.target.value || undefined)}
          >
            <option value="">Все</option>
            {UNTI_API_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the cat.2035 booking word of the
          row's УНТИ leaf-winner, THEIR vocabulary verbatim. The options come from the
          #3.1 facet (`useTransfertValues` — the DISTINCT words the api's own winner
          fold can match, so an option can never 400), plus the `none` bucket the
          contract's own literal names («Не указан» — the rows the register renders
          «—»). SERVER-SIDE like `contract` — the api narrows rows AND the pagination
          total in two phases (the mirror join is BY-VALUE), the select only writes
          the URL key; the active filter rides to the export, which narrows the file
          with the screen and gains the appended «Трансферт» column.
        */}
        <TransfertFilter params={params} onFilter={onFilter} />

        {/*
          A4/T.Z19 (2026-09-13) — the «Органик» tri-state is gone with the «Флаги»
          column it belonged to. `organic` itself is still in the frozen #3, so the
          api (and the export) keep honouring it; the register just no longer offers
          a control for a flag nobody on this screen can see any more.
        */}
        <div className="filter-bar__field filter-bar__field--narrow">
          <label className="filter-bar__label" htmlFor="applications-format">
            Формат обучения
          </label>
          <Select
            id="applications-format"
            className={filterAppliedClass(params[PARAM.FORMAT])}
            data-testid="filter-format"
            value={params[PARAM.FORMAT] ?? ''}
            onChange={(event) => onFilter(PARAM.FORMAT, event.target.value || undefined)}
          >
            <option value="">Любой</option>
            {/* T.Z99 (v1.9.19) — the last option is the literal `none` bucket: the rows
                the exports never named a format for (the register renders «—»). */}
            {FORMAT_FILTER_OPTIONS.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </Select>
        </div>

        {/* ADDITIVE (v1.8.0, Р9/#3, T.Y10) — «Статус менеджера»: a dictionary id or the
            «без статуса» bucket. One combobox, one key on the wire — the two never meet. */}
        <ManagerStatusFilter params={params} onFilter={onFilter} onFilterPatch={onFilterPatch} />

        {/*
          T.Z59 — the «Сортировка» selectbox is gone: the column headers of the table
          below are the sort control now (`sortBy`/`order` of #3, server-side over ALL
          filtered rows). The URL keys live in the model (`PARAM.SORT_BY` / `PARAM.ORDER`);
          the frozen `sort` key is read from a bookmarked link only.
        */}

        <button type="button" className="btn btn--ghost btn--sm" data-testid="filter-reset" onClick={onReset}>
          Сбросить
        </button>

        {/* T.Z219 — the show/hide control for the table's columns, next to the
            reset. Presentation only: the api still returns every field. */}
        {columnsControl}
      </div>
    </div>
  );
}

/**
 * The leading «Площадка» row. The options come from the venue directory (#6 —
 * names AND addresses, the dictionary the quota pills' page reads). An id the URL
 * carries but that dictionary does not know stays selectable anyway: the name of
 * the venue the loaded rows show is injected, and as the last resort the id
 * itself becomes the label — a scoped link must never read as «nothing selected».
 *
 * T.Z20 — the list leads with «Без площадки», the contract's own `?hasVenue=false`
 * bucket: the owner reads the unlinked applicants off this control first, before
 * any typing. The state and its URL patch are the model's (`venueFilterValue` /
 * `venueFilterPatch`), which keeps one rule: a venue and «Без площадки» are
 * mutually exclusive, because `venueId=N` AND `hasVenue=false` is an empty set —
 * so one commit always writes the chosen key and clears the other, together.
 */
function VenueFilter({
  params,
  venueNames,
  onFilter,
  onFilterPatch,
}: {
  params: Record<string, string>;
  venueNames?: ReadonlyMap<number, string>;
  onFilter: (key: string, value: string | undefined) => void;
  onFilterPatch?: (patch: Record<string, string | undefined>) => void;
}) {
  const directory = useVenueDirectory();
  const value = venueFilterValue(params);
  const none = value === VENUE_FILTER_NONE;

  const options = venueComboboxOptions(directory.data ?? []);
  const known = value === '' || none || options.some((option) => option.value === value);
  const venueId = Number.parseInt(value, 10);
  const label =
    value === '' || none || known ? undefined : (venueNames?.get(venueId) ?? `Площадка ${value}`);

  // «Без площадки» leads the list whatever else is in it — even the injected
  // fallback label of an unknown id stays below it (the owner reads this bucket
  // first, before any venue and before any typing).
  const list = label
    ? [NO_VENUE_OPTION, { value, label }, ...options]
    : [NO_VENUE_OPTION, ...options];

  /** One selection, one URL write — the committed key and the cleared key together. */
  const commit = (next: string) => {
    const patch = venueFilterPatch(next);
    if (onFilterPatch) {
      onFilterPatch(patch);
      return;
    }
    for (const key of [PARAM.VENUE_ID, PARAM.HAS_VENUE] as const) {
      onFilter(key, patch[key]);
    }
  };

  return (
    <div className="filter-bar__row">
      <div className="filter-bar__field filter-bar__field--venue">
        <label className="filter-bar__label" htmlFor="applications-venue">
          Площадка
        </label>
        <Combobox
          id="applications-venue"
          testId="filter-venue"
          className={filterAppliedClass(value)}
          value={value}
          options={list}
          onChange={commit}
          placeholder="Все площадки — найдите по названию или адресу"
          emptyText="Площадка не найдена"
          clearLabel="Показать все площадки"
          resultsLabel={(count) => `Найдено площадок: ${count}`}
        />
      </div>
    </div>
  );
}

/**
 * ADDITIVE (v1.8.0, Р9/#3, T.Y10) — the «Статус менеджера» filter.
 *
 * A combobox over the ACTIVE half of the dictionary (#28) plus the «Без статуса»
 * option, which is the contract's own `?managerStatus=none` bucket (Р9 made it
 * mandatory: more than a thousand applications carry no status at all). Choosing a
 * status writes `?managerStatusId=`; the OTHER key is always cleared by
 * `managerStatusFilterPatch`, because the api answers 400 when both travel.
 *
 * An id the URL carries but the dictionary does not know (a status deactivated
 * since the link was shared) stays selectable as «Статус N» — a filter must never
 * read as «nothing selected», the same rule the venue filter follows with row names.
 */
function ManagerStatusFilter({
  params,
  onFilter,
  onFilterPatch,
}: {
  params: Record<string, string>;
  onFilter: (key: string, value: string | undefined) => void;
  onFilterPatch?: (patch: Record<string, string | undefined>) => void;
}) {
  const dictionary = useManagerStatusDictionary();
  const value = managerStatusFilterValue(params);
  const options = managerStatusFilterOptions(dictionary.data);
  const known = value === '' || options.some((option) => option.value === value);
  const visible = known ? options : [{ value, label: `Статус ${value}` }, ...options];

  /** One selection, one URL write — the swapped-in key and the dropped key together. */
  const commit = (next: string) => {
    const patch = managerStatusFilterPatch(next);
    if (onFilterPatch) {
      onFilterPatch(patch);
      return;
    }
    for (const key of [PARAM.MANAGER_STATUS, PARAM.MANAGER_STATUS_ID] as const) {
      onFilter(key, patch[key]);
    }
  };

  return (
    <div className="filter-bar__field">
      <label className="filter-bar__label" htmlFor="applications-manager-status">
        Статус менеджера
      </label>
      <Combobox
        id="applications-manager-status"
        testId="filter-managerStatus"
        className={filterAppliedClass(value)}
        value={value}
        options={visible}
        onChange={commit}
        placeholder="Все статусы"
        emptyText="Статус не найден"
        clearLabel="Показать все статусы, включая «без статуса»"
        ariaLabel="Статус менеджера"
        resultsLabel={(count) => `Найдено статусов: ${count}`}
      />
    </div>
  );
}

/**
 * ADDITIVE (v1.9.87, T.Z237) — the «Трансферт» filter: a select over the #3.1
 * facet's DISTINCT winner-fold words (`useTransfertValues`), the values verbatim
 * (their vocabulary, never translated — the cell prints the same string), plus the
 * contract's own `none` bucket («Не указан») for the rows the register renders «—».
 * A value the URL carries that the facet does not know (a hand-edited link, a word
 * the mirror has stopped carrying) stays selectable as itself — a filter must
 * never read as «nothing selected», the venue/program-short rescue. A failing or
 * 403'd facet read degrades to the `none` option alone (`retry: 0`), the page
 * otherwise untouched — the «Рассылка» index precedent.
 */
function TransfertFilter({
  params,
  onFilter,
}: {
  params: Record<string, string>;
  onFilter: (key: string, value: string | undefined) => void;
}) {
  const values = useTransfertValues();
  const value = params[PARAM.TRANSFERT] ?? '';
  const options = transfertFilterOptions(values.data ?? [], params[PARAM.TRANSFERT]);

  return (
    <div className="filter-bar__field">
      <label className="filter-bar__label" htmlFor="applications-transfert">
        Трансферт
      </label>
      <Select
        id="applications-transfert"
        className={filterAppliedClass(value)}
        data-testid="filter-transfert"
        value={value}
        onChange={(event) => onFilter(PARAM.TRANSFERT, event.target.value || undefined)}
      >
        <option value="">Все</option>
        <option value={TRANSFERT_FILTER_NONE}>{TRANSFERT_NONE_LABEL}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/**
 * ADDITIVE (v1.9.56, T.Z186) — the «Рассылка» filter: a select over the Списки
 * рассылок (#34.1) by NAME — the owner reads lists by their names, never by ids, and
 * the api narrows by the id the option carries. An id the URL carries that the index
 * does not know (a hand-typed link) stays selectable as «Рассылка N» — a filter must
 * never read as «nothing selected», the same rescue the venue / program-short /
 * manager-status filters apply. The index is a MANAGER+ surface, so a VIEWER session's
 * 403 keeps the select on «Все» alone (one quiet refusal, `useMailingListIndex`).
 */
function MailingListFilter({
  params,
  onFilter,
}: {
  params: Record<string, string>;
  onFilter: (key: string, value: string | undefined) => void;
}) {
  const index = useMailingListIndex();
  const value = params[PARAM.MAILING_LIST] ?? '';
  const options = mailingListFilterOptions(index.data ?? [], value);

  return (
    <div className="filter-bar__field">
      <label className="filter-bar__label" htmlFor="applications-mailing-list">
        Рассылка
      </label>
      <Select
        id="applications-mailing-list"
        className={filterAppliedClass(value)}
        data-testid="filter-mailingList"
        value={value}
        onChange={(event) => onFilter(PARAM.MAILING_LIST, event.target.value || undefined)}
      >
        <option value="">Все</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/**
 * ADDITIVE (v1.8.9, T.Z56) — the «Отдел» filter: the five buckets of
 * `src/departments.ts` («Все» / ОП / ОС / «Без отдела» / «Смешанный») with the counts
 * over the #6 × #25.5 join (`useDepartmentJoin`). The api does the actual narrowing —
 * an unknown value can never reach it, because the URL parse already folds it back to
 * «Все» (the same «never 400s» rule `audience` follows).
 */
function DepartmentFilter({
  params,
  onFilter,
}: {
  params: Record<string, string>;
  onFilter: (key: string, value: string | undefined) => void;
}) {
  const { counts } = useDepartmentJoin();
  const options = departmentFilterOptions(counts);
  const value = params[PARAM.DEPARTMENT] ?? '';
  const known = value === '' || options.some((option) => option.value === value);
  const visible = known ? options : [{ value, label: value, title: undefined }, ...options];

  return (
    <div className="filter-bar__field filter-bar__field--narrow">
      <label className="filter-bar__label" htmlFor="applications-department">
        Отдел
      </label>
      <Select
        id="applications-department"
        className={filterAppliedClass(value)}
        data-testid="filter-department"
        value={value}
        onChange={(event) => onFilter(PARAM.DEPARTMENT, event.target.value || undefined)}
      >
        {visible.map((option) => (
          <option key={option.value} value={option.value} title={option.title}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/**
 * ADDITIVE (v1.8.8, T.Z43) — the «Программа» filter: a combobox over the DISTINCT
 * `programs.short_name` captions of #21 (six of them over the 12 courses — each caption
 * covers the офл/онл pair). The committed value travels verbatim as `?programShort=`
 * and the api resolves it to course ids; a caption the dictionary has stopped carrying
 * would 400, so a stale link's value stays selectable below the list (the same
 * «never reads as nothing selected» rule the venue and manager-status filters follow).
 */
function ProgramShortFilter({
  params,
  onFilter,
}: {
  params: Record<string, string>;
  onFilter: (key: string, value: string | undefined) => void;
}) {
  const programs = usePrograms();
  const options = programShortOptions(programs.data?.data ?? []);
  const value = params[PARAM.PROGRAM_SHORT] ?? '';
  const known = value === '' || options.some((option) => option.value === value);
  const visible = known ? options : [{ value, label: value }, ...options];

  return (
    <div className="filter-bar__field">
      <label className="filter-bar__label" htmlFor="applications-program">
        Программа
      </label>
      <Combobox
        id="applications-program"
        testId="filter-programShort"
        className={filterAppliedClass(value)}
        value={value}
        options={visible}
        onChange={(next) => onFilter(PARAM.PROGRAM_SHORT, next === '' ? undefined : next)}
        placeholder="Все программы"
        emptyText="Программа не найдена"
        clearLabel="Показать все программы"
        ariaLabel="Программа"
        resultsLabel={(count) => `Найдено программ: ${count}`}
      />
    </div>
  );
}

/**
 * Uncontrolled text input that commits on Enter / blur. `key` is the URL value, so
 * a filter arriving from outside (a dashboard drill-down, a reset) re-mounts the
 * input with the URL's value instead of keeping the user's stale typing.
 *
 * T.Z192 — the «applied» tint derives from that same URL value here (`value` IS
 * the control's filter state), so the three free-text filters need no plumbing of
 * their own.
 */
function CommitInput({
  id,
  testId,
  value,
  placeholder,
  inputMode,
  onCommit,
}: {
  id: string;
  testId: string;
  value: string;
  placeholder?: string;
  inputMode?: 'numeric' | 'text';
  onCommit: (value: string | undefined) => void;
}) {
  const commit = (raw: string) => onCommit(raw.trim() === '' ? undefined : raw.trim());

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(event.currentTarget.value);
    }
  };

  return (
    <Input
      key={value}
      id={id}
      className={filterAppliedClass(value)}
      data-testid={testId}
      defaultValue={value}
      placeholder={placeholder}
      inputMode={inputMode}
      onBlur={(event) => commit(event.currentTarget.value)}
      onKeyDown={onKeyDown}
    />
  );
}
